import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { getSimilarVehiclesByMakeAndModel } from 'Services/api/vehicles';
import type { GetSimilarVehiclesResponseWithVersion } from 'Services/api/vehicles.types';
import { logger } from 'Services/logger/logger';
import isClientSideAuth from 'Utilities/helpers/isClientSideAuth';

interface UseFetchSimilarVehiclesByMakeAndModelProps {
	normalisedMake: string;
	normalisedModel: string;
}

export const useFetchSimilarVehiclesByMakeAndModel = (
	params: UseFetchSimilarVehiclesByMakeAndModelProps,
): UseQueryResult<GetSimilarVehiclesResponseWithVersion> => {
	const { normalisedMake, normalisedModel } = params;

	return useQuery({
		enabled: Boolean(normalisedMake),
		onError: (error: AxiosError) => {
			const message = `There was an issue when fetching similar vehicles by make and model with the params: ${JSON.stringify(
				params,
			)}`;

			logger.error({
				context: { extra: { info: message } },
				error,
				message,
				scope: 'useFetchSimilarVehiclesByMakeAndModel',
			});
		},
		queryFn: () =>
			getSimilarVehiclesByMakeAndModel({
				make: normalisedMake,
				model: normalisedModel,
			}),
		queryKey: ['similarVehiclesByMakeAndModel', normalisedMake, normalisedModel],
		refetchOnWindowFocus: () => isClientSideAuth(),
	});
};
